import { useState, useCallback } from 'react';
import axiosInstance from '../axiosInstance';
import { endpoints } from '../endPoints';
import useToggle from 'shared/hooks/use-toggle';
import { toast } from 'react-hot-toast';

/** Header value: registry tenant UUID (same as selected row id on Tenants table). */
export const tenantUsersHeaders = (registryTenantId) => ({
  'X-TenantID': String(registryTenantId),
});

const enumLabel = (v) =>
  typeof v === 'string' ? v : v?.status ?? v?.name ?? String(v ?? '');

/**
 * Fetches tenant users with status ACTIVE for dropdowns (e.g. assign to group).
 * Uses the same id as central `user` after sync, suitable for `/groups/users/{id}/groups`.
 *
 * @param {string} registryTenantId — registry UUID (X-TenantID)
 * @param {{ page?: number, size?: number }} [opts]
 * @returns {Promise<Array<{ id: string, name: string, email: string, status: string }>>}
 */
/**
 * GET tenant user by id (requires X-TenantID).
 * @returns {Promise<object|null>}
 */
export async function getTenantUserById(registryTenantId, userId) {
  if (!registryTenantId || !userId) return null;
  const response = await axiosInstance.get(`${endpoints.tenantUsers.list}/${userId}`, {
    headers: tenantUsersHeaders(registryTenantId),
  });
  return response.data?.data ?? response.data ?? null;
}

/**
 * PUT /tenant-users — full TenantUserDto body (id, email required by backend).
 */
export async function updateTenantUserRecord(registryTenantId, payload) {
  if (!registryTenantId) {
    throw new Error('Missing tenant id');
  }
  const response = await axiosInstance.put(endpoints.tenantUsers.list, payload, {
    headers: tenantUsersHeaders(registryTenantId),
  });
  return response.data?.data ?? response.data;
}

/**
 * Saves profile image; returns photo URL path from API. Requires X-TenantID for tenant DB resolution.
 */
export async function uploadTenantUserPhoto(registryTenantId, userId, file) {
  if (!registryTenantId || !userId || !file) {
    throw new Error('Missing tenant id, user id, or file');
  }
  if (!file.type.startsWith('image/')) {
    throw new Error('Please select a valid image file');
  }
  if (file.size > 5 * 1024 * 1024) {
    throw new Error('File size must be less than 5MB');
  }
  const formData = new FormData();
  formData.append('file', file);
  const url = `${endpoints.files.photo}?id=${encodeURIComponent(String(userId))}`;
  const response = await axiosInstance.put(url, formData, {
    headers: {
      ...tenantUsersHeaders(registryTenantId),
    },
    timeout: 30000,
  });
  const path = typeof response.data === 'string' ? response.data : response.data?.data;
  return path ?? '';
}

export async function fetchActiveTenantUsers(registryTenantId, opts = {}) {
  if (!registryTenantId || String(registryTenantId).trim() === '') {
    return [];
  }
  const page = opts.page ?? 0;
  const size = opts.size ?? 500;
  const rid = String(registryTenantId).trim();
  const url = `${endpoints.tenantUsers.list}?page=${page}&size=${size}&status=${encodeURIComponent('ACTIVE')}&tenantId=${encodeURIComponent(rid)}`;
  const response = await axiosInstance.get(url, {
    headers: tenantUsersHeaders(registryTenantId),
  });
  const payload = response.data?.data;
  const list = Array.isArray(payload?.data) ? payload.data : [];
  return list
    .filter((u) => {
      const s = String(enumLabel(u.status) ?? '').toUpperCase();
      return s === 'ACTIVE';
    })
    .map((u) => ({
      id: String(u.id),
      name: u.name ?? '',
      email: u.email ?? '',
      jobPosition: u.jobPosition ?? u.job_position ?? '',
      status: String(enumLabel(u.status) ?? ''),
    }));
}

/**
 * GET /api/v1/tenant-users — lists users in the tenant DB for the given registry UUID.
 * Pass registryTenantId from the route (tenant row id); each request sets X-TenantID.
 */
const useTenantUserService = (registryTenantId) => {
  const [users, setUsers] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, toggleLoading] = useToggle();

  const getTenantUser = useCallback(
    async (id) => {
      if (!registryTenantId || !id) {
        return null;
      }
      const response = await axiosInstance.get(
        `${endpoints.tenantUsers.list}/${id}`,
        { headers: tenantUsersHeaders(registryTenantId) }
      );
      return response.data?.data ?? response.data;
    },
    [registryTenantId]
  );

  const updateTenantUser = useCallback(
    async (payload) => {
      if (!registryTenantId) {
        throw new Error('Missing tenant id');
      }
      const response = await axiosInstance.put(
        endpoints.tenantUsers.list,
        payload,
        { headers: tenantUsersHeaders(registryTenantId) }
      );
      return response.data;
    },
    [registryTenantId]
  );

  const deleteTenantUser = useCallback(
    async (id) => {
      if (!registryTenantId || !id) {
        throw new Error('Missing tenant id or user id');
      }
      await axiosInstance.delete(`${endpoints.tenantUsers.list}/${id}`, {
        headers: tenantUsersHeaders(registryTenantId),
      });
    },
    [registryTenantId]
  );

  const createTenantUser = useCallback(
    async ({ name, email, password, tenantName }) => {
      if (!registryTenantId) {
        throw new Error('Missing tenant id');
      }
      const payload = {
        tenantId: String(registryTenantId),
        name,
        email,
        password,
        ...(tenantName ? { tenantName } : {}),
      };
      const response = await axiosInstance.post(endpoints.tenantUsers.list, payload, {
        headers: tenantUsersHeaders(registryTenantId),
      });
      return response.data;
    },
    [registryTenantId]
  );

  const fetchTenantUsers = useCallback(
    async (page, size, status = null) => {
      if (!registryTenantId) {
        setUsers([]);
        setTotalRecords(0);
        return;
      }
      toggleLoading();
      try {
        let url = `${endpoints.tenantUsers.list}?page=${page}&size=${size}`;
        if (status) {
          url += `&status=${encodeURIComponent(status)}`;
        }
        url += `&tenantId=${encodeURIComponent(registryTenantId)}`;
        const response = await axiosInstance.get(url, {
          headers: tenantUsersHeaders(registryTenantId),
        });
        const { data } = response.data;
        setTotalRecords(Number(data?.records) || 0);
        const list = Array.isArray(data?.data) ? data.data : [];
        setUsers(
          list.map((u) => ({
            id: u.id,
            name: u.name ?? '',
            email: u.email ?? '',
            phoneNumber: u.phoneNumber ?? u.phone_number ?? '',
            jobPosition: u.jobPosition ?? u.job_position ?? '',
            role: enumLabel(u.role),
            status: enumLabel(u.status),
            createdAt: u.createdAt ?? '',
            verifiedAt: u.verifiedAt ?? '',
            tenantId: u.tenantId ?? '',
            tenantName: u.tenantName ?? '',
            photoUrl: u.photoUrl ?? '',
          }))
        );
      } catch (error) {
        toast.error(
          error.response?.data?.message || 'Failed to load tenant users.'
        );
        console.error('Fetch tenant users error:', error);
        setUsers([]);
        setTotalRecords(0);
      } finally {
        toggleLoading();
      }
    },
    [registryTenantId, toggleLoading]
  );

  return {
    users,
    totalRecords,
    fetchTenantUsers,
    createTenantUser,
    getTenantUser,
    updateTenantUser,
    deleteTenantUser,
    isLoading,
  };
};

export default useTenantUserService;
